import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { Session, Customer, loginShopifyAppProxy, getCustomer, listCharges, Charge, productSearch, Subscription } from '@rechargeapps/storefront-client';
import { popsclTrack } from '../../scripts/utils/popscl';
import { projectedOrders } from '../utils/rewardsConfig';

export type portalEvent = {
  eventName:
    'subscription_created' |
    'subscription_activated' |
    'subscription_updated' |
    'subscription_charge_skipped' |
    'subscription_cancellation_initiated' |
    'subscription_paused' |
    'remove_subscription' |
    'charge_unskipped' |
    'charge_skipped' |
    'page_view' |
    'onetime_dialog_opened' |
    'onetime_dialog_closed' |
    'subscription_dialog_opened' |
    'subscription_dialog_closed' |
    'banner_carousel_scrolled' |
    'banner_rendered' |
    'pack_cancel_pop-up' |
    'routine_builder_cancel_pop-up' |
    'exited_pop-up' |
    'delayed_1_week' |
    'delayed_2_week' |
    'selected_date' |
    'proceed_cancel' |
    'change_frequency_clicked' |
    'frequency_changed' |
    'subscription_cancelled' |
    'keep_subscription' |
    'keep_subscription_discount_applied' |
    'due_date_cta_clicked' |
    'gift_cta_clicked' |
    'recipe_card_clicked' |
    'course_card_clicked';
  eventData: Record<string, any>;
}

type ShopifyAddress = {
  name: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  zip: string;
}

export type ShopifyOrder = {
  id: string;
  name: string;
  order_number: string;
  created_at: string;
  total_price: string;
  subtotal_price: string;
  shop_address: ShopifyAddress;
  line_items_subtotal_price: string;
  billing_address: ShopifyAddress;
  shipping_address: ShopifyAddress;
  cancelled: boolean;
  cancelled_at: string;
  total_refunded_amount: string;
  tax_price: string;
  line_items: {
    id: string;
    title: string;
    quantity: number;
    price: string;
    product_id?: number;
    image?: {
      url: string;
    }
  }[];
  shipping_price: string;
  discount_applications: {
    title: string;
    amount: string;
    type: string;
    value: string;
    value_type: string;
  }[];
}

interface GlobalContextType {
  session: Session | null;
  customer: Customer | null;
  loading: boolean;
  loadingStates: {
    session: boolean;
    orders: boolean;
    charges: boolean;
    customer: boolean;
  };
  orders: {
    previous: ShopifyOrder[] | null;
    upcoming: Charge[] | null;
  } | null;
  currentPage: 'home' | 'upcomingOrders' | 'previousOrders' | 'shippingAndBilling' | 'manageSubscriptions' | 'bumpcollective' | 'cancelSubscription';
  processedOrder: Charge | null;
  dispatch: React.Dispatch<any>;
  refreshData: () => Promise<void>;
  sendEvent: (eventName: portalEvent['eventName'], eventData?: portalEvent['eventData']) => Promise<void>;
}
const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const trimesterProductVariantIds = [
  '35141160370325', // first trimester
  '35141162107029', // second trimester
  '35141171871893', // third trimester
]

export function GlobalProvider({ children }: { children: React.ReactNode }) {
  // get url path
  const urlPath = window.location.pathname;
  // const directories = urlPath.split('/');
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get('page') || 'home';

  const initialState = useRef<GlobalContextType>({
    session: null,
    customer: null,
    orders: { previous: null, upcoming: null },
    loading: true,
    loadingStates: {
      session: true,
      orders: true,
      charges: true,
      customer: true,
    },
    currentPage: page,
    processedOrder: null,
    dispatch: () => {},
    refreshData: () => Promise.resolve(),
    sendEvent: () => Promise.resolve(),
  });
  
  function reducer(state: GlobalContextType, action: any) {
    switch (action.type) {
      case 'SET_ALL':
        return { ...state, ...action.payload };
      case 'SET_SESSION':
        return { ...state, session: action.payload };
      case 'SET_CUSTOMER':
        return { ...state, customer: action.payload };
      case 'SET_ORDERS':
        return { ...state, orders: action.payload };
      case 'SET_ORDERS_ONLY':
        return { 
          ...state, 
          orders: { ...state.orders, previous: action.payload }
        };
      case 'SET_CHARGES_ONLY':
        return { 
          ...state, 
          orders: { ...state.orders, upcoming: action.payload }
        };
      case 'SET_LOADING':
        return { ...state, loading: action.payload };
      case 'SET_LOADING_STATE':
        return {
          ...state,
          loadingStates: {
            ...state.loadingStates,
            [action.payload.key]: action.payload.value,
          },
        };
      case 'SET_CURRENT_PAGE':
        window.scrollTo(0, 0);
        // upodate the url path
        if (action.payload === 'home') {
          window.history.pushState({}, '', '/pages/account');
        } else {
          const params = new URLSearchParams({ page: action.payload, ...action.params });
          window.history.pushState({}, '', `/pages/account?${params.toString()}`);
        }
        return { ...state, currentPage: action.payload, processedOrder: null };
      case 'SET_PROCESSED_ORDER':
        return { ...state, processedOrder: action.payload };
      default:
        return state;
    }
  }
  
  const [state, dispatch] = useReducer(reducer, initialState.current);

  const loadPreviousOrders = async () => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'orders', value: true } });
      
      const response = await fetch(window.Shopify.routes.root + "?sections=orders-json&em-bypass=server");
      const ordersJson = await response.json();
      const htmlString = ordersJson["orders-json"];
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      const sectionContent = tempDiv.querySelector('#shopify-section-orders-json')?.innerHTML;
      
      let shopifyOrders: ShopifyOrder[] | null = null;
      if (sectionContent) {
        const cleanedContent = sectionContent.trim().replace(/[\u0000-\u001F]/g, '');
        try {
          shopifyOrders = JSON.parse(cleanedContent);
        } catch (parseError) {
          console.error("portal: JSON parse error:", parseError);
          shopifyOrders = [];
        }
      }
      
      dispatch({ type: 'SET_ORDERS_ONLY', payload: shopifyOrders });
    } catch (error) {
      console.error("portal: error loading orders:", error);
    } finally {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'orders', value: false } });
    }
  };

  const loadUpcomingCharges = async (session: Session) => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'charges', value: true } });
      
      const upcomingCharges = await listCharges(session, { 
        limit: 250, 
        sort_by: 'scheduled_at-asc', 
        status: ['queued', 'pending', 'skipped', 'error'], 
        scheduled_at_min: new Date(Date.now() - 86400000).toISOString().split('T')[0] + 'T00:00:00Z' 
      });
      
      dispatch({ type: 'SET_CHARGES_ONLY', payload: upcomingCharges.charges });
    } catch (error) {
      console.error("portal: error loading charges:", error);
    } finally {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'charges', value: false } });
    }
  };

  const loadCustomerData = async (session: Session) => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'customer', value: true } });
      
      const customerData = await getCustomer(session, { include: ['subscriptions', 'addresses', 'payment_methods'] });

      // Fetch product images for subscriptions
      const rechargeProductsData = await productSearch(session, { 
        format_version: '2022-06',
        external_product_ids: customerData?.include?.subscriptions?.map((subscription) => subscription.external_product_id.ecommerce),
        product_published_status: 'any'
      });

      customerData.include.subscriptions = customerData.include?.subscriptions.map((subscription) => {
        const externalProductId = subscription.external_product_id.ecommerce;
        return {
          ...subscription,
          product: rechargeProductsData.products.find((product) => product.external_product_id === externalProductId),
        }
      });

      // add the expected_due dates to the customer
      const skuOrder = ['WTR1-P', 'WTR2-P', 'WTR3-P'];
      const activeTrimesterSubscriptions = customerData?.include?.subscriptions
        ?.filter(({ status, external_variant_id }) => {
          const isActive = status === 'active';
          const isTrimester = trimesterProductVariantIds.includes(external_variant_id?.ecommerce || '');
          return isActive && isTrimester;
        })
        ?.sort((a, b) => {
          const aIndex = skuOrder.indexOf(a.sku);
          const bIndex = skuOrder.indexOf(b.sku);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
 
      const dueDates: { subscription: Subscription, dueDate: string }[] = [];
      activeTrimesterSubscriptions?.forEach((subscription) => {
        const dueDate = subscription.properties.find(({ name }) => name === 'expected_date');
        if (!dueDates.some(d => d.dueDate === (dueDate?.value || ''))) {
          dueDates.push({
            subscription,
            dueDate: dueDate?.value || '',
          });
        }
      });

      customerData.dueDates = dueDates;

      let currentStageSubscription = activeTrimesterSubscriptions?.[0] || null;
      let currentStage = currentStageSubscription?.sku || false;
      if (!currentStage) {
        const momMultiSupport = customerData?.include?.subscriptions?.find((subscription: Subscription) => subscription.external_variant_id.ecommerce === '35141176426645');
        if (momMultiSupport) currentStage = momMultiSupport.sku || false;
        currentStageSubscription = momMultiSupport || currentStageSubscription || null;
      }

      customerData.currentStageSubscription = currentStageSubscription;
      customerData.currentStage = currentStage;
      
      dispatch({ type: 'SET_CUSTOMER', payload: customerData as Customer });
    } catch (error) {
      console.error("portal: error loading customer data:", error);
    } finally {
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'customer', value: false } });
    }
  };

  const initializeData = async () => {
    try {
      // Load session first (required for auth)
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'session', value: true } });
      const newSession = await loginShopifyAppProxy();
      dispatch({ type: 'SET_SESSION', payload: newSession });
      dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'session', value: false } });
      
      // Set main loading to false once session is ready
      dispatch({ type: 'SET_LOADING', payload: false });

      // If no customer ID, only load Shopify orders
      if (!newSession.customerId) {
        loadPreviousOrders();
        dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'charges', value: false } });
        dispatch({ type: 'SET_LOADING_STATE', payload: { key: 'customer', value: false } });
        return;
      }

      // Load data in parallel, each updates UI as it completes
      loadPreviousOrders();
      loadUpcomingCharges(newSession);
      loadCustomerData(newSession);
    } catch (error) {
      console.error("portal: error initializing:", error);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const refreshData = async () => {
    await initializeData();
  };

  const sendEvent = async (eventName: portalEvent['eventName'], eventData?: portalEvent['eventData']) => {
    const trackingData = {
      ...eventData,
      source_context: 'customer_portal',
      page: state.currentPage,
    }

    const uuid = state.customer?.id;
    popsclTrack(eventName, uuid, trackingData);

    try {
      const originalDiscounts = eventData?.original_discount_codes;

      // Add polling logic for add_subscription event
      if (
        eventName === 'subscription_created' || 
        eventName === 'subscription_activated' || 
        eventName === 'subscription_updated' || 
        eventName === 'subscription_charge_skipped' || 
        eventName === 'subscription_cancellation_initiated' || 
        eventName === 'remove_subscription' ||
        eventName === 'charge_unskipped' ||
        eventName === 'charge_skipped'
      ) {
        let attempts = 0;
        const maxAttempts = 20;

        const checkForDiscount = async () => {
          try {
            const session = state.session;
            if (!session) return;
            const charges = await listCharges(session, { limit: 250, sort_by: 'scheduled_at-asc', status: ['queued', 'pending', 'skipped'], scheduled_at_min: new Date().toISOString().split('T')[0] + 'T00:00:00Z' });
            const currentDiscounts = charges.charges.flatMap(charge => charge.discounts || []);
            const hasDiscountChanged = originalDiscounts?.some(original => 
              !currentDiscounts?.includes(original)
            ) || currentDiscounts?.some(current => 
              !originalDiscounts?.includes(current)
            );

            if (hasDiscountChanged) {
              // Discounts have changed, refresh data and stop polling
              await refreshData();
              return;
            }

            attempts++;
            if (attempts < maxAttempts) {
              // Continue polling every 3 seconds
              setTimeout(checkForDiscount, 3000);
            }
          } catch (error) {
            console.error('Error checking for discount:', error);
          }
        };

        // Start polling
        checkForDiscount();
      }
    } catch (error) {
      console.error('Error sending event:', error);
    }
  };

  useEffect(() => {
    initializeData();
  }, []);

  useEffect(() => {
    sendEvent('page_view', { page: state.currentPage });
  }, [state.currentPage]);  

  return (
    <GlobalContext.Provider value={{ ...state, dispatch, refreshData, sendEvent }}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobal() {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
}
